import React, { useState } from 'react';

import { resolveActionPending } from '../../services/api/visitActionPendingApi';
import {
  INTENT_LABELS,
  RESOLUTIONS,
} from '../../constants/visitActionPending';

/**
 * ResolveActionPendingDialog — close (RESOLVE) or abort (CANCEL) the
 * open overlay on a visit. Used by the current overlay holder.
 *
 * FORWARD is a separate route; in v1 we surface "Send to next" as a
 * convenience that opens `ActionPendingDialog` instead — keeps the
 * resolve flow simple. (Server still accepts FORWARD on the resolve
 * endpoint; we just don't expose it here.)
 *
 * Props:
 *   visit
 *   onSubmitted — (response) => void
 *   onCancel    — () => void
 */
export default function ResolveActionPendingDialog({ visit, onSubmitted, onCancel }) {
  const [resolution, setResolution] = useState(RESOLUTIONS.RESOLVED);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const intentLabel = INTENT_LABELS[visit?.action_pending_intent] || visit?.action_pending_intent;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrorMsg(null);
    try {
      const res = await resolveActionPending(visit.visit_id, {
        resolution,
        comment: comment.trim() || null,
      });
      onSubmitted?.(res);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setErrorMsg(typeof detail === 'string' ? detail : 'Failed to resolve');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-1">Resolve action</h2>
        {intentLabel && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Action: {intentLabel}
          </p>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Outcome *</label>
          <div className="flex gap-2">
            <label className="flex-1 cursor-pointer">
              <input
                type="radio"
                name="resolution"
                value={RESOLUTIONS.RESOLVED}
                checked={resolution === RESOLUTIONS.RESOLVED}
                onChange={() => setResolution(RESOLUTIONS.RESOLVED)}
                className="sr-only peer"
              />
              <div className="px-3 py-2 rounded-md border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-slate-800 peer-checked:bg-emerald-100 peer-checked:dark:bg-emerald-900/40 peer-checked:border-emerald-500 text-sm">
                ✓ Done
              </div>
            </label>
            <label className="flex-1 cursor-pointer">
              <input
                type="radio"
                name="resolution"
                value={RESOLUTIONS.CANCEL}
                checked={resolution === RESOLUTIONS.CANCEL}
                onChange={() => setResolution(RESOLUTIONS.CANCEL)}
                className="sr-only peer"
              />
              <div className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 peer-checked:bg-slate-100 peer-checked:dark:bg-slate-700 peer-checked:border-slate-500 text-sm">
                ✗ Cancel
              </div>
            </label>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Comment (optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            placeholder={
              resolution === RESOLUTIONS.RESOLVED
                ? 'e.g. ₹500 collected — sending back'
                : 'Why are you cancelling?'
            }
          />
        </div>

        {errorMsg && (
          <div className="mb-3 rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
            {errorMsg}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Close
          </button>
          <button
            type="submit"
            disabled={busy}
            className={[
              'px-4 py-1.5 text-sm rounded-md text-white disabled:opacity-60',
              resolution === RESOLUTIONS.RESOLVED
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-slate-600 hover:bg-slate-700',
            ].join(' ')}
          >
            {busy ? 'Saving…' : (resolution === RESOLUTIONS.RESOLVED ? 'Mark done' : 'Cancel action')}
          </button>
        </div>
      </form>
    </div>
  );
}
